import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Einsatz, Mitarbeiter } from '../types';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const TYP_FARBE: Record<Einsatz['typ'], string> = {
  MONTAGE: 'var(--blue, #3b6ea5)',
  SERVICE: 'var(--green-500, #4a7c59)',
  NOTDIENST: 'var(--red, #a54242)',
  WARTUNG: 'var(--amber, #a58a3b)',
};

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

function montagDerWoche(offsetWochen: number): Date {
  const heute = new Date();
  const tag = (heute.getDay() + 6) % 7; // Mo=0
  const montag = new Date(heute);
  montag.setDate(heute.getDate() - tag + offsetWochen * 7);
  montag.setHours(0, 0, 0, 0);
  return montag;
}

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Formatiert ein Date für ein <input type="datetime-local"> (lokale Zeit, keine tz-Konvertierung).
function isoLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const leeresFormular = { beginn: '08:00', ende: '12:00', typ: 'SERVICE', einsatzort: '', beschreibung: '' };

const DISPO_ROLLEN = ['Admin', 'Geschäftsführer', 'Projektleiter'];

interface EinsatzPlantafelProps {
  userRolle?: string;
}

const EinsatzPlantafel: React.FC<EinsatzPlantafelProps> = ({ userRolle }) => {
  // SuperAdmin wird wie Admin behandelt (Sonderfall, falls er hier auftaucht).
  const darfDisponieren = !userRolle
    || userRolle === 'SuperAdmin'
    || DISPO_ROLLEN.includes(userRolle);
  const [woche, setWoche] = useState(0);
  const [einsaetze, setEinsaetze] = useState<Einsatz[]>([]);
  const [monteure, setMonteure] = useState<Mitarbeiter[]>([]);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [modal, setModal] = useState<{ mitarbeiterId: number; datum: Date } | null>(null);
  const [detail, setDetail] = useState<Einsatz | null>(null);
  const [form, setForm] = useState(leeresFormular);
  const [saving, setSaving] = useState(false);
  const [abschluss, setAbschluss] = useState<{ einsatz: Einsatz; ist_beginn: string; ist_ende: string } | null>(null);

  const montag = montagDerWoche(woche);
  const tage = WOCHENTAGE.map((_, i) => {
    const d = new Date(montag);
    d.setDate(montag.getDate() + i);
    return d;
  });

  const load = useCallback(() => {
    setLoading(true);
    setFehler(null);
    const von = new Date(montag);
    const bis = new Date(montag);
    bis.setDate(bis.getDate() + 7);
    Promise.all([
      axios.get<Einsatz[]>(`${API_URL}/api/einsaetze`, {
        params: { von: `${iso(von)}T00:00:00`, bis: `${iso(bis)}T00:00:00` },
      }),
      axios.get<Mitarbeiter[]>(`${API_URL}/api/mitarbeiter`),
    ])
      .then(([e, m]) => {
        setEinsaetze(e.data.filter(x => x.status !== 'STORNIERT'));
        setMonteure(m.data.filter(x => x.aktiv));
      })
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        setFehler(`Plantafel konnte nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woche]);

  useEffect(() => { load(); }, [load]);

  const bloecke = (mitarbeiterId: number, tag: Date): Einsatz[] =>
    einsaetze.filter(e => e.mitarbeiter_id === mitarbeiterId && iso(new Date(e.beginn)) === iso(tag));

  const handleAnlegen = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!modal) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/einsaetze`, {
        mitarbeiter_id: modal.mitarbeiterId,
        beginn: `${iso(modal.datum)}T${form.beginn}:00`,
        ende: `${iso(modal.datum)}T${form.ende}:00`,
        typ: form.typ,
        einsatzort: form.einsatzort || null,
        beschreibung: form.beschreibung || null,
      });
      setModal(null);
      setForm(leeresFormular);
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Fehler';
      alert(`Einsatz konnte nicht angelegt werden: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleStorno = async (e: Einsatz) => {
    const grund = window.prompt(`Einsatz #${e.einsatz_id} stornieren.\nGrund der Stornierung (optional):`);
    if (grund === null) return;
    try {
      await axios.post(`${API_URL}/api/einsaetze/${e.einsatz_id}/storno`, { grund });
      setDetail(null);
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Fehler';
      alert(`Einsatz konnte nicht storniert werden: ${msg}`);
    }
  };

  const handleStatusWechsel = async (e: Einsatz, status: 'BESTAETIGT' | 'UNTERWEGS') => {
    try {
      await axios.patch(`${API_URL}/api/einsaetze/${e.einsatz_id}`, { status });
      setDetail(null);
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Fehler';
      alert(`Status konnte nicht geändert werden: ${msg}`);
    }
  };

  const openAbschluss = (e: Einsatz) => {
    // Ist-Zeiten vorbefüllt mit den Sollzeiten (AP 3.4) — im Dialog änderbar.
    setAbschluss({
      einsatz: e,
      ist_beginn: isoLocalInput(new Date(e.beginn)),
      ist_ende: isoLocalInput(new Date(e.ende)),
    });
  };

  const handleAbschliessen = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!abschluss) return;
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/einsaetze/${abschluss.einsatz.einsatz_id}`, {
        status: 'ABGESCHLOSSEN',
        ist_beginn: abschluss.ist_beginn,
        ist_ende: abschluss.ist_ende,
      });
      setAbschluss(null);
      setDetail(null);
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Fehler';
      alert(`Einsatz konnte nicht abgeschlossen werden: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncRetry = async (e: Einsatz) => {
    try {
      await axios.post(`${API_URL}/api/einsaetze/${e.einsatz_id}/sync`);
      setDetail(null);
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Fehler';
      alert(`Outlook-Sync konnte nicht erneut angestoßen werden: ${msg}`);
    }
  };

  if (loading) return <div style={{ padding: 16 }}>Lade Plantafel…</div>;

  if (fehler) {
    return (
      <div style={{ padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'var(--bone-2)', border: '1px solid var(--bone-3)', fontSize: 13, color: 'var(--red)' }}>
          <span>{fehler}</span>
          <button onClick={load} style={{ marginLeft: 'auto', padding: '4px 12px', border: '1px solid var(--red)', background: 'none', color: 'var(--red)', fontSize: 12, cursor: 'pointer' }}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Einsatzplanung</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setWoche(w => w - 1)} style={{ padding: '4px 12px', cursor: 'pointer' }}>← Woche</button>
          <span style={{ fontSize: 13, minWidth: 170, textAlign: 'center' }}>
            KW ab {montag.toLocaleDateString('de-DE')} {woche === 0 && '(aktuell)'}
          </span>
          <button onClick={() => setWoche(w => w + 1)} style={{ padding: '4px 12px', cursor: 'pointer' }}>Woche →</button>
        </div>
      </div>

      {monteure.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>
          Keine aktiven Monteure angelegt. Bitte zuerst Mitarbeiter erfassen.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ background: 'var(--bone-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', width: 150 }}>Monteur</th>
                {tage.map((t, i) => (
                  <th key={i} style={{ padding: '8px 6px', textAlign: 'center', fontSize: 12 }}>
                    {WOCHENTAGE[i]} {t.getDate()}.{t.getMonth() + 1}.
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {monteure.map(m => (
                <tr key={m.mitarbeiter_id} style={{ borderBottom: '1px solid var(--bone-3)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500, verticalAlign: 'top' }}>{m.name}</td>
                  {tage.map((tag, i) => (
                    <td key={i}
                        onClick={darfDisponieren ? () => setModal({ mitarbeiterId: m.mitarbeiter_id, datum: tag }) : undefined}
                        style={{ padding: 4, verticalAlign: 'top', cursor: darfDisponieren ? 'pointer' : 'default', minHeight: 60, borderLeft: '1px solid var(--bone-3)' }}>
                      {bloecke(m.mitarbeiter_id, tag).map(e => (
                        <div key={e.einsatz_id}
                             onClick={ev => { ev.stopPropagation(); setDetail(e); }}
                             title={`${e.typ} · ${e.status}`}
                             style={{ background: TYP_FARBE[e.typ], color: 'var(--bone, #fff)', padding: '4px 6px', marginBottom: 3, fontSize: 11, cursor: 'pointer', opacity: e.status === 'ABGESCHLOSSEN' ? 0.55 : 1 }}>
                          {new Date(e.beginn).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          –{new Date(e.ende).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                          {' '}{e.einsatzort || (e.auftrag_id ? `A-${e.auftrag_id}` : e.typ)}
                          {(e.outlook_sync_status === 'FEHLER' || e.outlook_sync_status === 'AUSSTEHEND') && (
                            <span title="Outlook-Sync ausstehend/fehlgeschlagen"> ⚠</span>
                          )}
                        </div>
                      ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && darfDisponieren && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
             onClick={() => setModal(null)}>
          <form onSubmit={handleAnlegen} onClick={ev => ev.stopPropagation()}
                style={{ background: 'var(--bone, #fff)', padding: 20, width: 380, border: '1px solid var(--bone-3)' }}>
            <h3 style={{ marginTop: 0 }}>
              Einsatz am {modal.datum.toLocaleDateString('de-DE')} —{' '}
              {monteure.find(m => m.mitarbeiter_id === modal.mitarbeiterId)?.name}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ fontSize: 12 }}>Beginn
                <input type="time" required value={form.beginn} onChange={e => setForm(f => ({ ...f, beginn: e.target.value }))} style={{ width: '100%', padding: 6 }} />
              </label>
              <label style={{ fontSize: 12 }}>Ende
                <input type="time" required value={form.ende} onChange={e => setForm(f => ({ ...f, ende: e.target.value }))} style={{ width: '100%', padding: 6 }} />
              </label>
              <label style={{ fontSize: 12, gridColumn: '1 / -1' }}>Typ
                <select value={form.typ} onChange={e => setForm(f => ({ ...f, typ: e.target.value }))} style={{ width: '100%', padding: 6 }}>
                  <option value="MONTAGE">Montage</option>
                  <option value="SERVICE">Service</option>
                  <option value="NOTDIENST">Notdienst</option>
                  <option value="WARTUNG">Wartung</option>
                </select>
              </label>
              <label style={{ fontSize: 12, gridColumn: '1 / -1' }}>Einsatzort
                <input value={form.einsatzort} onChange={e => setForm(f => ({ ...f, einsatzort: e.target.value }))} style={{ width: '100%', padding: 6 }} placeholder="Adresse / Kunde" />
              </label>
              <label style={{ fontSize: 12, gridColumn: '1 / -1' }}>Beschreibung
                <input value={form.beschreibung} onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))} style={{ width: '100%', padding: 6 }} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button type="submit" disabled={saving} style={{ padding: '8px 20px', cursor: 'pointer' }}>
                {saving ? 'Speichert…' : 'Einsatz planen'}
              </button>
              <button type="button" onClick={() => setModal(null)} style={{ padding: '8px 14px', cursor: 'pointer' }}>Abbrechen</button>
            </div>
          </form>
        </div>
      )}

      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}
             onClick={() => setDetail(null)}>
          <div onClick={ev => ev.stopPropagation()} style={{ background: 'var(--bone, #fff)', padding: 20, width: 380, border: '1px solid var(--bone-3)' }}>
            <h3 style={{ marginTop: 0 }}>Einsatz #{detail.einsatz_id} — {detail.mitarbeiter_name}</h3>
            <p style={{ fontSize: 13, margin: '4px 0' }}>
              {new Date(detail.beginn).toLocaleString('de-DE')} – {new Date(detail.ende).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}<br />
              Typ: {detail.typ} · Status: {detail.status}<br />
              {detail.einsatzort && <>Ort: {detail.einsatzort}<br /></>}
              {detail.beschreibung && <>Notiz: {detail.beschreibung}<br /></>}
              {detail.storno_grund && <>Storno-Grund: {detail.storno_grund}<br /></>}
              {detail.status === 'ABGESCHLOSSEN' && detail.ist_beginn && detail.ist_ende && (
                <>Ist-Zeit: {new Date(detail.ist_beginn).toLocaleString('de-DE')} – {new Date(detail.ist_ende).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}<br /></>
              )}
              Outlook: {detail.outlook_sync_status}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              {darfDisponieren && detail.outlook_sync_status === 'FEHLER' && (
                <button onClick={() => handleSyncRetry(detail)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Outlook-Sync erneut</button>
              )}
              {darfDisponieren && detail.status === 'GEPLANT' && (
                <button onClick={() => handleStatusWechsel(detail, 'BESTAETIGT')} style={{ padding: '6px 12px', cursor: 'pointer' }}>Bestätigen</button>
              )}
              {darfDisponieren && detail.status === 'BESTAETIGT' && (
                <button onClick={() => handleStatusWechsel(detail, 'UNTERWEGS')} style={{ padding: '6px 12px', cursor: 'pointer' }}>Unterwegs</button>
              )}
              {darfDisponieren && detail.status === 'UNTERWEGS' && (
                <button onClick={() => openAbschluss(detail)} style={{ padding: '6px 12px', cursor: 'pointer' }}>Abschließen</button>
              )}
              {darfDisponieren && detail.status !== 'ABGESCHLOSSEN' && detail.status !== 'STORNIERT' && (
                <button onClick={() => handleStorno(detail)} style={{ padding: '6px 12px', cursor: 'pointer', color: 'var(--red)', border: '1px solid var(--red)', background: 'none' }}>Stornieren</button>
              )}
              <button onClick={() => setDetail(null)} style={{ padding: '6px 12px', cursor: 'pointer', marginLeft: 'auto' }}>Schließen</button>
            </div>
          </div>
        </div>
      )}

      {abschluss && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 51 }}
             onClick={() => setAbschluss(null)}>
          <form onSubmit={handleAbschliessen} onClick={ev => ev.stopPropagation()}
                style={{ background: 'var(--bone, #fff)', padding: 20, width: 380, border: '1px solid var(--bone-3)' }}>
            <h3 style={{ marginTop: 0 }}>Einsatz #{abschluss.einsatz.einsatz_id} abschließen</h3>
            <p style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 0 }}>
              Ist-Zeiten prüfen/korrigieren — vorbefüllt mit den geplanten Zeiten.
              Erzeugt automatisch eine Zeitbuchung für die Nachkalkulation.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
              <label style={{ fontSize: 12 }}>Ist-Beginn
                <input type="datetime-local" required value={abschluss.ist_beginn}
                       onChange={e => setAbschluss(a => a && ({ ...a, ist_beginn: e.target.value }))}
                       style={{ width: '100%', padding: 6 }} />
              </label>
              <label style={{ fontSize: 12 }}>Ist-Ende
                <input type="datetime-local" required value={abschluss.ist_ende}
                       onChange={e => setAbschluss(a => a && ({ ...a, ist_ende: e.target.value }))}
                       style={{ width: '100%', padding: 6 }} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button type="submit" disabled={saving} style={{ padding: '8px 20px', cursor: 'pointer' }}>
                {saving ? 'Speichert…' : 'Abschließen'}
              </button>
              <button type="button" onClick={() => setAbschluss(null)} style={{ padding: '8px 14px', cursor: 'pointer' }}>Abbrechen</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EinsatzPlantafel;
